import { useAppForm } from '@massilia/features'
import { type GetMyProfileResponse, toError, useUpdateMyEmailsMutation } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useState } from 'react'
import { toast } from 'sonner'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { updateEmailsSchema } from '@/lib/schemas/personal-informations/personal-informations.schemas'
import { useAuthStore } from '@/stores/auth.store'

import { DisplayRow } from './display-row'
import { EditableSection } from './editable-section'

interface EmailsSectionProps {
  profile: GetMyProfileResponse
}

export function EmailsSection({ profile }: EmailsSectionProps) {
  const [isEditing, setIsEditing] = useState(false)
  const { mutateAsync: updateEmails } = useUpdateMyEmailsMutation()
  const { updateAuth, user } = useAuthStore()

  const form = useAppForm({
    defaultValues: {
      email: profile.user.email,
      secondaryEmail: profile.client?.secondaryEmail ?? '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      try {
        await updateEmails({
          email: value.email,
          secondaryEmail: value.secondaryEmail || null,
          password: value.password,
        })
        if (user) {
          updateAuth({
            isAuthenticated: true,
            user: { ...user, email: value.email },
          })
        }
        setIsEditing(false)
      } catch (error) {
        toast.error(resolveAuthErrorMessage(toError(error)))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate({ validationSource: 'form', value }, updateEmailsSchema),
    },
  })

  const handleCancel = () => {
    form.reset()
    setIsEditing(false)
  }

  return (
    <EditableSection
      actionLabel="Modifier les adresses"
      isEditing={isEditing}
      onToggleEdit={() => (isEditing ? handleCancel() : setIsEditing(true))}
      editForm={
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField label="Adresse e-mail #1" placeholder="email@example.com" />
            )}
          />
          <form.AppField
            name="secondaryEmail"
            children={(field) => (
              <field.TextField label="Adresse e-mail #2" placeholder="email@example.com" optional />
            )}
          />
          <form.AppField
            name="password"
            children={(field) => (
              <field.PasswordField label="Votre mot de passe" placeholder="Mot de passe actuel" />
            )}
          />
          <div className="flex gap-3">
            <form.Subscribe selector={(state) => state.isSubmitting}>
              {(isSubmitting) => (
                <>
                  <Button
                    className="bg-brand-600 text-white hover:bg-brand-700"
                    disabled={isSubmitting}
                    type="submit"
                  >
                    {isSubmitting ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    disabled={isSubmitting}
                    onClick={handleCancel}
                    type="button"
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </>
              )}
            </form.Subscribe>
          </div>
        </form>
      }
    >
      <div className="flex flex-col">
        <DisplayRow label="Adresse e-mail #1" value={profile.user.email} />
        <DisplayRow
          label="Adresse e-mail #2"
          sublabel="Facultatif"
          value={profile.client?.secondaryEmail}
        />
      </div>
    </EditableSection>
  )
}
